import React from 'react';
import {
  Box,
  Checkbox,
  Flex,
  Input,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';
import { PLATFORMS, PLATFORM_GROUPS } from '../data/platforms/index';

/** Section header that mimics a fieldset legend with a horizontal rule */
const GroupHeader = ({ label }) => {
  const lineColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  return (
    <Flex align="center" gap={3} mb={3}>
      <Text fontSize="sm" fontWeight="semibold" color={textColor} whiteSpace="nowrap">
        {label}
      </Text>
      <Box flex={1} h="1px" bg={lineColor} />
    </Flex>
  );
};

const PlatformRow = observer(({ platformId, store }) => {
  const platform = PLATFORMS[platformId];
  if (!platform) return null;

  const isSelected = store.selectedPlatforms.has(platformId);
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Flex align="center" py={1.5} gap={2}>
      <Checkbox
        isChecked={isSelected}
        onChange={() => store.togglePlatform(platformId)}
        colorScheme="blue"
        size="md"
      >
        <Flex align="baseline" gap={1.5}>
          <Text fontSize="sm" fontWeight="medium">{platform.label}</Text>
          <Text fontSize="xs" color={mutedColor}>
            — {platform.icons.length} different sizes and files
          </Text>
        </Flex>
      </Checkbox>
    </Flex>
  );
});

const PlatformSelector = observer(({ store }) => {
  const inputBg = useColorModeValue('white', 'gray.800');
  const hintColor = useColorModeValue('gray.400', 'gray.500');

  return (
    <Box>
      {PLATFORM_GROUPS.map((group) => (
        <Box key={group.id} mb={5}>
          <GroupHeader label={group.label} />

          {group.platformIds.map((id) => (
            <PlatformRow key={id} platformId={id} store={store} />
          ))}

          {/* Android filename input */}
          {group.hasFilenameOption && store.selectedPlatforms.has('android') && (
            <Box mt={3} pl={6}>
              <Flex align="center" gap={3}>
                <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')} whiteSpace="nowrap">
                  File name
                </Text>
                <Input
                  size="sm"
                  bg={inputBg}
                  value={store.androidFilename}
                  onChange={(e) => store.setAndroidFilename(e.target.value)}
                  placeholder="ic_launcher"
                  maxW="200px"
                  borderRadius="md"
                />
              </Flex>
              <Text fontSize="xs" color={hintColor} mt={1} pl={0}>
                Change file name for all generated Android images
              </Text>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
});

export default PlatformSelector;
